import React, {
    useEffect,
    useState
} from 'react';
import {
    Link,
    useNavigate,
    useParams
} from 'react-router-dom';
import './adminNotice.css';
import API_BASE_URL from '../../../config/api';

const AdminNoticeForm = () => {
    const navigate =
        useNavigate();

    const { id } =
        useParams();

    const isEditMode =
        Boolean(id);

    const [formData, setFormData] =
        useState({
            noticeType: '안내',
            title: '',
            writer: '관리자',
            content: ''
        });

    const [isLoading, setIsLoading] =
        useState(isEditMode);

    const [isSubmitting, setIsSubmitting] =
        useState(false);

    /*
     * ================================================
     * EDIT DATA
     * ================================================
     */

    useEffect(() => {
        if (!isEditMode) {
            return;
        }

        const fetchNotice = async () => {
            try {
                setIsLoading(true);

                const response = await fetch(
                    `${API_BASE_URL}/api/notices/admin/${id}`,
                    {
                        method: 'GET',
                        credentials: 'include'
                    }
                );

                const result =
                    await response.json();

                if (!response.ok) {
                    throw new Error(
                        result.message ||
                        '공지사항을 불러오지 못했습니다.'
                    );
                }

                const notice =
                    result.notice;

                setFormData({
                    noticeType:
                        notice.notice_type ||
                        '안내',
                    title:
                        notice.title ||
                        '',
                    writer:
                        notice.writer ||
                        '관리자',
                    content:
                        notice.content ||
                        ''
                });
            } catch (error) {
                console.error(
                    '공지사항 조회 오류:',
                    error
                );

                alert(
                    error.message
                );

                navigate(
                    '/admin/notices'
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchNotice();
    }, [
        id,
        isEditMode,
        navigate
    ]);

    /*
     * ================================================
     * CHANGE
     * ================================================
     */

    const handleChange = (event) => {
        const {
            name,
            value
        } = event.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    /*
     * ================================================
     * SUBMIT
     * ================================================
     */

    const handleSubmit = async (
        event
    ) => {
        event.preventDefault();

        if (
            !formData.title.trim()
        ) {
            alert(
                '제목을 입력해주세요.'
            );

            return;
        }

        if (
            !formData.content.trim()
        ) {
            alert(
                '내용을 입력해주세요.'
            );

            return;
        }

        try {
            setIsSubmitting(true);

            const url =
                isEditMode
                    ? `${API_BASE_URL}/api/notices/admin/${id}`
                    : `${API_BASE_URL}/api/notices/admin`;

            const response = await fetch(
                url,
                {
                    method:
                        isEditMode
                            ? 'PUT'
                            : 'POST',
                    headers: {
                        'Content-Type':
                            'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        noticeType:
                            formData.noticeType,
                        title:
                            formData.title,
                        writer:
                            formData.writer,
                        content:
                            formData.content
                    })
                }
            );

            const result =
                await response.json();

            if (!response.ok) {
                throw new Error(
                    result.message ||
                    (
                        isEditMode
                            ? '공지사항 수정에 실패했습니다.'
                            : '공지사항 등록에 실패했습니다.'
                    )
                );
            }

            alert(
                isEditMode
                    ? '공지사항이 수정되었습니다.'
                    : '공지사항이 등록되었습니다.'
            );

            navigate(
                '/admin/notices'
            );
        } catch (error) {
            console.error(
                '공지사항 저장 오류:',
                error
            );

            alert(
                error.message
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="admin-notice">

                <div className="admin-notice-empty">

                    <i className="mdi mdi-loading mdi-spin"></i>

                    <strong>
                        공지사항을 불러오는 중입니다.
                    </strong>

                </div>

            </div>
        );
    }

    return (
        <div className="admin-notice">

            <div className="admin-notice-header">

                <div>
                    <p>
                        NOTICE
                    </p>

                    <h1>
                        {isEditMode
                            ? '공지사항 수정'
                            : '공지사항 등록'
                        }
                    </h1>

                    <span>
                        {isEditMode
                            ? '등록된 공지사항의 내용을 수정합니다.'
                            : '홈페이지에 노출할 공지사항을 등록합니다.'
                        }
                    </span>
                </div>

                <Link
                    to="/admin/notices"
                    className="admin-notice-back"
                >
                    <i className="mdi mdi-arrow-left"></i>

                    목록으로
                </Link>

            </div>

            <form
                className="admin-notice-form"
                onSubmit={handleSubmit}
            >

                <div className="admin-notice-form-section">

                    <div className="admin-notice-form-heading">

                        <h2>
                            기본 정보
                        </h2>

                        <p>
                            공지사항의 기본 정보를 입력해주세요.
                        </p>

                    </div>

                    <div className="admin-notice-form-grid">

                        <div className="admin-notice-form-group">

                            <label htmlFor="noticeType">
                                구분
                            </label>

                            <input
                                id="noticeType"
                                type="text"
                                name="noticeType"
                                value={
                                    formData.noticeType
                                }
                                onChange={
                                    handleChange
                                }
                                placeholder="예: 안내"
                            />

                        </div>

                        <div className="admin-notice-form-group">

                            <label htmlFor="writer">
                                작성자
                            </label>

                            <input
                                id="writer"
                                type="text"
                                name="writer"
                                value={
                                    formData.writer
                                }
                                onChange={
                                    handleChange
                                }
                                placeholder="관리자"
                            />

                        </div>

                        <div className="admin-notice-form-group full">

                            <label htmlFor="title">
                                제목
                                <em>*</em>
                            </label>

                            <input
                                id="title"
                                type="text"
                                name="title"
                                value={
                                    formData.title
                                }
                                onChange={
                                    handleChange
                                }
                                placeholder="공지사항 제목을 입력해주세요."
                            />

                        </div>

                        <div className="admin-notice-form-group full">

                            <label htmlFor="content">
                                내용
                                <em>*</em>
                            </label>

                            <textarea
                                id="content"
                                name="content"
                                value={
                                    formData.content
                                }
                                onChange={
                                    handleChange
                                }
                                placeholder="공지사항 내용을 입력해주세요."
                            />

                        </div>

                    </div>

                </div>

                <div className="admin-notice-form-actions">

                    <Link
                        to="/admin/notices"
                        className="admin-notice-form-cancel"
                    >
                        취소
                    </Link>

                    <button
                        type="submit"
                        className="admin-notice-form-submit"
                        disabled={
                            isSubmitting
                        }
                    >
                        {isSubmitting
                            ? (
                                isEditMode
                                    ? '수정 중...'
                                    : '등록 중...'
                            )
                            : (
                                isEditMode
                                    ? '수정하기'
                                    : '등록하기'
                            )
                        }
                    </button>

                </div>

            </form>

        </div>
    );
};

export default AdminNoticeForm;